/**
 * Lookups slice — account/workspace-scoped reference lists that other entities
 * reference by id.
 *
 * Today: course authors. As their domains migrate, the same shape (an entity
 * adapter + a fetch-once thunk + CRUD thunks) hosts custom-field definitions,
 * professions, and reference-field definitions here.
 *
 * State is normalized: entities store the foreign id (e.g. `course.authorId`),
 * this slice is the single source of truth for the list, and components
 * denormalize at read time by selecting the item by id. Editing a record here
 * updates every consumer at once — no embedded copies to drift.
 *
 * Each list is fetched once via a `condition`-guarded thunk, so any component
 * can dispatch the fetch on mount without duplicate requests. The whole slice
 * resets when the session ends (the profile `clearUser` action).
 */

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  type EntityState,
} from '@reduxjs/toolkit';
import {
  CourseAuthor,
  CourseCustomField,
  Profession,
  SaveCourseAuthorRequest,
  SaveCourseCustomFieldRequest,
  SaveReferenceFieldRequest,
  UserReferenceField,
} from '../../apis/api-client';
import {
  courseAuthorsClient,
  courseCustomFieldsClient,
  professionsClient,
  referenceFieldsClient,
} from '../../apis/client-instances';
import type { RootState } from '../../types';
import { clearUser } from '../profile/profileSlice';

type LoadStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

// ── Course authors ────────────────────────────────────────────────────────
const authorsAdapter = createEntityAdapter<CourseAuthor, number>({
  selectId: (author) => author.id!,
  sortComparer: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
});

// ── User reference fields (workspace-scoped definitions) ───────────────────
const referenceFieldsAdapter = createEntityAdapter<UserReferenceField, number>({
  selectId: (field) => field.id!,
  sortComparer: (a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0),
});

// ── Professions (account-scoped) ───────────────────────────────────────────
const professionsAdapter = createEntityAdapter<Profession, number>({
  selectId: (profession) => profession.id!,
  sortComparer: (a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0),
});

// ── Course custom-field definitions (account-scoped) ───────────────────────
const customFieldsAdapter = createEntityAdapter<CourseCustomField, number>({
  selectId: (field) => field.id!,
  sortComparer: (a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0),
});

export interface LookupsState {
  authors: EntityState<CourseAuthor, number> & { status: LoadStatus };
  referenceFields: EntityState<UserReferenceField, number> & { status: LoadStatus };
  professions: EntityState<Profession, number> & { status: LoadStatus };
  customFields: EntityState<CourseCustomField, number> & { status: LoadStatus };
}

const initialState: LookupsState = {
  authors: authorsAdapter.getInitialState({ status: 'idle' as LoadStatus }),
  referenceFields: referenceFieldsAdapter.getInitialState({ status: 'idle' as LoadStatus }),
  professions: professionsAdapter.getInitialState({ status: 'idle' as LoadStatus }),
  customFields: customFieldsAdapter.getInitialState({ status: 'idle' as LoadStatus }),
};

export const fetchAuthors = createAsyncThunk(
  'lookups/fetchAuthors',
  () => courseAuthorsClient.list(),
  {
    // Fetch-once: skip when a load is in flight or already succeeded.
    condition: (_arg, { getState }) => {
      const status = (getState() as RootState).lookups.authors.status;
      return status === 'idle' || status === 'failed';
    },
  },
);

export const createAuthor = createAsyncThunk(
  'lookups/createAuthor',
  (request: SaveCourseAuthorRequest) => courseAuthorsClient.create(request),
);

export const updateAuthor = createAsyncThunk(
  'lookups/updateAuthor',
  ({ id, request }: { id: number; request: SaveCourseAuthorRequest }) =>
    courseAuthorsClient.update(id, request),
);

export const deleteAuthor = createAsyncThunk('lookups/deleteAuthor', async (id: number) => {
  await courseAuthorsClient.delete(id);
  return id;
});

export const fetchReferenceFields = createAsyncThunk(
  'lookups/fetchReferenceFields',
  () => referenceFieldsClient.list(undefined),
  {
    condition: (_arg, { getState }) => {
      const status = (getState() as RootState).lookups.referenceFields.status;
      return status === 'idle' || status === 'failed';
    },
  },
);

export const createReferenceField = createAsyncThunk(
  'lookups/createReferenceField',
  (request: SaveReferenceFieldRequest) => referenceFieldsClient.create(request),
);

export const updateReferenceField = createAsyncThunk(
  'lookups/updateReferenceField',
  ({ id, request }: { id: number; request: SaveReferenceFieldRequest }) =>
    referenceFieldsClient.update(id, request),
);

export const deleteReferenceField = createAsyncThunk(
  'lookups/deleteReferenceField',
  async (id: number) => {
    await referenceFieldsClient.delete(id);
    return id;
  },
);

export const fetchProfessions = createAsyncThunk(
  'lookups/fetchProfessions',
  () => professionsClient.list(),
  {
    condition: (_arg, { getState }) => {
      const status = (getState() as RootState).lookups.professions.status;
      return status === 'idle' || status === 'failed';
    },
  },
);

export const fetchCustomFields = createAsyncThunk(
  'lookups/fetchCustomFields',
  () => courseCustomFieldsClient.list(),
  {
    condition: (_arg, { getState }) => {
      const status = (getState() as RootState).lookups.customFields.status;
      return status === 'idle' || status === 'failed';
    },
  },
);

export const createCustomField = createAsyncThunk(
  'lookups/createCustomField',
  (request: SaveCourseCustomFieldRequest) => courseCustomFieldsClient.create(request),
);

export const updateCustomField = createAsyncThunk(
  'lookups/updateCustomField',
  ({ id, request }: { id: number; request: SaveCourseCustomFieldRequest }) =>
    courseCustomFieldsClient.update(id, request),
);

export const deleteCustomField = createAsyncThunk(
  'lookups/deleteCustomField',
  async (id: number) => {
    await courseCustomFieldsClient.delete(id);
    return id;
  },
);

const lookupsSlice = createSlice({
  name: 'lookups',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuthors.pending, (state) => {
        state.authors.status = 'loading';
      })
      .addCase(fetchAuthors.fulfilled, (state, action) => {
        authorsAdapter.setAll(state.authors, action.payload);
        state.authors.status = 'succeeded';
      })
      .addCase(fetchAuthors.rejected, (state) => {
        state.authors.status = 'failed';
      })
      .addCase(createAuthor.fulfilled, (state, action) => {
        authorsAdapter.addOne(state.authors, action.payload);
      })
      .addCase(updateAuthor.fulfilled, (state, action) => {
        authorsAdapter.setOne(state.authors, action.payload);
      })
      .addCase(deleteAuthor.fulfilled, (state, action) => {
        authorsAdapter.removeOne(state.authors, action.payload);
      })
      .addCase(fetchReferenceFields.pending, (state) => {
        state.referenceFields.status = 'loading';
      })
      .addCase(fetchReferenceFields.fulfilled, (state, action) => {
        referenceFieldsAdapter.setAll(state.referenceFields, action.payload);
        state.referenceFields.status = 'succeeded';
      })
      .addCase(fetchReferenceFields.rejected, (state) => {
        state.referenceFields.status = 'failed';
      })
      .addCase(createReferenceField.fulfilled, (state, action) => {
        referenceFieldsAdapter.addOne(state.referenceFields, action.payload);
      })
      .addCase(updateReferenceField.fulfilled, (state, action) => {
        referenceFieldsAdapter.setOne(state.referenceFields, action.payload);
      })
      .addCase(deleteReferenceField.fulfilled, (state, action) => {
        referenceFieldsAdapter.removeOne(state.referenceFields, action.payload);
      })
      .addCase(fetchProfessions.pending, (state) => {
        state.professions.status = 'loading';
      })
      .addCase(fetchProfessions.fulfilled, (state, action) => {
        professionsAdapter.setAll(state.professions, action.payload);
        state.professions.status = 'succeeded';
      })
      .addCase(fetchProfessions.rejected, (state) => {
        state.professions.status = 'failed';
      })
      .addCase(fetchCustomFields.pending, (state) => {
        state.customFields.status = 'loading';
      })
      .addCase(fetchCustomFields.fulfilled, (state, action) => {
        customFieldsAdapter.setAll(state.customFields, action.payload);
        state.customFields.status = 'succeeded';
      })
      .addCase(fetchCustomFields.rejected, (state) => {
        state.customFields.status = 'failed';
      })
      .addCase(createCustomField.fulfilled, (state, action) => {
        customFieldsAdapter.addOne(state.customFields, action.payload);
      })
      .addCase(updateCustomField.fulfilled, (state, action) => {
        customFieldsAdapter.setOne(state.customFields, action.payload);
      })
      .addCase(deleteCustomField.fulfilled, (state, action) => {
        customFieldsAdapter.removeOne(state.customFields, action.payload);
      })
      // End of session — drop every cached list.
      .addCase(clearUser, () => initialState);
  },
});

export default lookupsSlice.reducer;

// ── Selectors ──────────────────────────────────────────────────────────────
const authorSelectors = authorsAdapter.getSelectors(
  (state: RootState) => state.lookups.authors,
);

export const selectAllAuthors = authorSelectors.selectAll;
/** id → author map, for resolving many ids in one pass (e.g. table rows). */
export const selectAuthorEntities = authorSelectors.selectEntities;
export const selectAuthorsStatus = (state: RootState): LoadStatus =>
  state.lookups.authors.status;

/** Resolve an author by id (e.g. `course.authorId`) — the read-time join. */
export const selectAuthorById = (
  state: RootState,
  id: number | null | undefined,
): CourseAuthor | undefined =>
  id == null ? undefined : authorSelectors.selectById(state, id);

const referenceFieldSelectors = referenceFieldsAdapter.getSelectors(
  (state: RootState) => state.lookups.referenceFields,
);
export const selectAllReferenceFields = referenceFieldSelectors.selectAll;
export const selectReferenceFieldsStatus = (state: RootState): LoadStatus =>
  state.lookups.referenceFields.status;

const professionSelectors = professionsAdapter.getSelectors(
  (state: RootState) => state.lookups.professions,
);
export const selectAllProfessions = professionSelectors.selectAll;
/** id → profession map, for resolving target-profession ids to names. */
export const selectProfessionEntities = professionSelectors.selectEntities;

const customFieldSelectors = customFieldsAdapter.getSelectors(
  (state: RootState) => state.lookups.customFields,
);
export const selectAllCustomFields = customFieldSelectors.selectAll;
/** id → custom-field map, for resolving a value's fieldId to its definition. */
export const selectCustomFieldEntities = customFieldSelectors.selectEntities;
export const selectCustomFieldsStatus = (state: RootState): LoadStatus =>
  state.lookups.customFields.status;

// ── Convenience hook ─────────────────────────────────────────────────────
/**
 * Ensure the author directory is loaded. Safe to call from any consumer
 * (display surface or picker) on mount — the thunk's `condition` dedupes.
 */
export function useEnsureAuthors(): void {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchAuthors() as any);
  }, [dispatch]);
}

/** Ensure the reference-field definitions are loaded (see `useEnsureAuthors`). */
export function useEnsureReferenceFields(): void {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchReferenceFields() as any);
  }, [dispatch]);
}

/** Ensure the professions list is loaded (see `useEnsureAuthors`). */
export function useEnsureProfessions(): void {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchProfessions() as any);
  }, [dispatch]);
}

/** Ensure the custom-field definitions are loaded (see `useEnsureAuthors`). */
export function useEnsureCustomFields(): void {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchCustomFields() as any);
  }, [dispatch]);
}
